import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore, type UserRole } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    requiresAuth?: boolean
    roles?: UserRole[]
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/pages/auth/CallbackPage.vue'),
    meta: { public: true },
  },
  {
    path: '/unauthorized',
    name: 'unauthorized',
    component: () => import('@/pages/auth/UnauthorizedPage.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/pages/onboarding/OnboardingWizard.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/dashboard/DashboardPage.vue'),
      },
      {
        path: 'structures',
        name: 'structures',
        component: () => import('@/pages/structures/StructureListPage.vue'),
        meta: { roles: ['GESTIONNAIRE', 'ADMIN'] },
      },
      {
        path: 'structures/new',
        name: 'structure-create',
        component: () => import('@/pages/structures/StructureCreatePage.vue'),
        meta: { roles: ['GESTIONNAIRE', 'ADMIN'] },
      },
      {
        path: 'structures/:id',
        name: 'structure-detail',
        component: () => import('@/pages/structures/StructureDetailPage.vue'),
      },
      {
        path: 'structures/:id/edit',
        name: 'structure-edit',
        component: () => import('@/pages/structures/StructureEditPage.vue'),
        meta: { roles: ['GESTIONNAIRE', 'ADMIN'] },
      },
      {
        path: 'structures/:structureId/personnel',
        name: 'personnel-list',
        component: () => import('@/pages/personnel/PersonnelListPage.vue'),
      },
      {
        path: 'structures/:structureId/personnel/new',
        name: 'personnel-create',
        component: () => import('@/pages/personnel/PersonnelFormPage.vue'),
        meta: { roles: ['GESTIONNAIRE', 'ADMIN'] },
      },
      {
        path: 'structures/:structureId/personnel/:id/edit',
        name: 'personnel-edit',
        component: () => import('@/pages/personnel/PersonnelFormPage.vue'),
        meta: { roles: ['GESTIONNAIRE', 'ADMIN'] },
      },
      {
        path: 'referentiel',
        name: 'referentiel',
        component: () => import('@/pages/referentiel/ReferentielPage.vue'),
      },
      {
        path: 'referentiel/axes/:id',
        name: 'axe-detail',
        component: () => import('@/pages/referentiel/AxeDetailPage.vue'),
      },
      {
        path: 'evaluations',
        name: 'evaluations',
        component: () => import('@/pages/evaluations/EvaluationListPage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'evaluations/:id',
        name: 'evaluation',
        component: () => import('@/pages/evaluations/EvaluationPage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'evaluations/:id/result',
        name: 'evaluation-result',
        component: () => import('@/pages/evaluations/EvaluationResultPage.vue'),
      },
      {
        path: 'evaluations/:id/compare/:otherId',
        name: 'evaluation-compare',
        component: () => import('@/pages/evaluations/EvaluationComparePage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'echeances',
        name: 'echeances',
        component: () => import('@/pages/echeances/EcheancesListPage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'echeances/calendar',
        name: 'echeances-calendar',
        component: () => import('@/pages/echeances/EcheancesCalendarPage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/pages/notifications/NotificationsPage.vue'),
      },
      {
        path: 'pmi',
        name: 'pmi-dashboard',
        component: () => import('@/pages/pmi/PmiDashboardPage.vue'),
        meta: { roles: ['AGENT_PMI', 'RESPONSABLE_PMI'] },
      },
      {
        path: 'pmi/structures/:id',
        name: 'pmi-structure-detail',
        component: () => import('@/pages/pmi/PmiStructureDetailPage.vue'),
        meta: { roles: ['AGENT_PMI', 'RESPONSABLE_PMI'] },
      },
      {
        path: 'pmi/analytics',
        name: 'pmi-analytics',
        component: () => import('@/pages/pmi/PmiAnalyticsPage.vue'),
        meta: { roles: ['RESPONSABLE_PMI'] },
      },
      {
        path: 'pmi/inspections',
        name: 'inspections',
        component: () => import('@/pages/pmi/InspectionListPage.vue'),
        meta: { roles: ['AGENT_PMI', 'RESPONSABLE_PMI'] },
      },
      {
        path: 'pmi/inspections/calendar',
        name: 'inspections-calendar',
        component: () => import('@/pages/pmi/InspectionCalendarPage.vue'),
        meta: { roles: ['AGENT_PMI'] },
      },
      {
        path: 'pmi/inspections/priorites',
        name: 'inspections-priorites',
        component: () => import('@/pages/pmi/InspectionPrioritesPage.vue'),
        meta: { roles: ['AGENT_PMI'] },
      },
      {
        path: 'pmi/rapports/:id',
        name: 'rapport-view',
        component: () => import('@/pages/pmi/RapportViewPage.vue'),
        meta: { roles: ['AGENT_PMI', 'RESPONSABLE_PMI', 'GESTIONNAIRE'] },
      },
      {
        path: 'pmi/rapports/:id/edit',
        name: 'rapport-edit',
        component: () => import('@/pages/pmi/RapportEditPage.vue'),
        meta: { roles: ['AGENT_PMI'] },
      },
      {
        path: 'pmi/observations',
        name: 'observations',
        component: () => import('@/pages/pmi/ObservationsPage.vue'),
        meta: { roles: ['AGENT_PMI', 'RESPONSABLE_PMI'] },
      },
      {
        path: 'messagerie',
        name: 'conversations',
        component: () => import('@/pages/messagerie/ConversationListPage.vue'),
      },
      {
        path: 'messagerie/:id',
        name: 'conversation',
        component: () => import('@/pages/messagerie/ConversationPage.vue'),
      },
      {
        path: 'facturation',
        name: 'subscription',
        component: () => import('@/pages/facturation/SubscriptionPage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'facturation/factures',
        name: 'invoices',
        component: () => import('@/pages/facturation/InvoicesPage.vue'),
        meta: { roles: ['GESTIONNAIRE'] },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/pages/admin/AdminUsersPage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'admin/departements',
        name: 'admin-departements',
        component: () => import('@/pages/admin/AdminDepartementsPage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'admin/referentiel',
        name: 'admin-referentiel',
        component: () => import('@/pages/admin/AdminReferentielPage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'admin/metrics',
        name: 'admin-metrics',
        component: () => import('@/pages/admin/AdminMetricsPage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'admin/api-keys',
        name: 'admin-api-keys',
        component: () => import('@/pages/admin/AdminApiKeysPage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'admin/veille',
        name: 'admin-veille',
        component: () => import('@/pages/admin/AdminVeillePage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'admin/audit',
        name: 'admin-audit',
        component: () => import('@/pages/admin/AdminAuditPage.vue'),
        meta: { roles: ['ADMIN'] },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/settings/SettingsPage.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()

  if (!authStore.initialized) {
    await authStore.initialize()
  }

  if (to.meta.public) return true

  if (to.matched.some((r) => r.meta.requiresAuth) && !authStore.isAuthenticated) {
    return { name: 'login' }
  }

  const requiredRoles = to.meta.roles as UserRole[] | undefined
  if (requiredRoles && requiredRoles.length > 0 && !authStore.hasRole(requiredRoles)) {
    return { name: 'unauthorized' }
  }

  if (
    authStore.isGestionnaire &&
    authStore.user?.isOnboarded === false &&
    to.name !== 'onboarding'
  ) {
    return { name: 'onboarding' }
  }

  return true
})

export default router
